<template>
	<div class="rolex-appointment-wizard" :class="[`step-${currentStep}`]">
		<!-- Pulsante Back fisso in alto a sinistra -->
		<div class="back-button-container" v-if="currentStep > 1">
			<button class="secondary-cta no-hover btn-black btn-back" @click="prevStep">
				<picture>
					<img :src="arrowIcon" alt="Icona" class="no-over">
				</picture>
				<span>
					{{ $t("common.back") }}
				</span>
			</button>

		</div>

		<div class="wizard-container">
			<div class="wizard-header">
				<h2 class="wizard-subtitle body24-bold">{{ $t("appointment.book_appointment") }}</h2>
				<h2 class="wizard-title headline50">{{ $t(steps[currentStep - 1].translationKey) }}</h2>
			</div>

			<div class="wizard-body">
				<!-- Step 1: Purpose -->
				<appointment-purpose v-if="currentStep === 1" :purposes="purposes" :current-appointment="currentAppointment"
					@select-purpose="selectPurpose" @proceed-next="nextStep" />

				<!-- Step 2: First Notice -->
				<appointment-notice v-if="currentStep === 2" :notice="notices.availability"
					@accept-notice="acceptAvailabilityNotice" @proceed-next="nextStep" />

				<!-- Step 3: Second Notice -->
				<appointment-notice v-if="currentStep === 3" :notice="notices.service" @accept-notice="acceptServiceNotice"
					@proceed-next="nextStep" />

				<!-- Step 4: Store Selection -->
				<store-selector v-if="currentStep === 4" :stores="storeLocations" :selected-country="selectedCountry"
					:current-appointment="currentAppointment" @select-store="selectStore" @select-country="selectCountry" />

				<!-- Step 5: Date Time Selection -->
				<date-time-selector v-if="currentStep === 5" :available-dates="availableDates" :available-times="availableTimes"
					:selected-date="selectedDate" :selected-time="selectedTime" :current-appointment="currentAppointment"
					@select-date="selectDate" @select-time="selectTime" />

				<!-- Step 6: Customer Info -->
				<customer-info-form v-if="currentStep === 6" ref="customerInfoForm" :current-appointment="currentAppointment"
					@form-validity-changed="updateFormValidity" />

				<!-- Step 7: Confirmation -->
				<appointment-confirmation v-if="currentStep === 7" :appointment="appointmentDetails"
					@add-to-calendar="addToCalendar" @modify-appointment="modifyAppointment" />
			</div>

			<div class="wizard-footer" v-if="currentStep < 7 && !isNoticeStep && !isPurposeStep">
				<button class="primary-cta" :disabled="!canProceed" @click="nextStep">
					{{ currentStep === 6 ? $t("common.confirm") : $t("common.next") }}
					<img :src="arrowIconWhite" alt="Icona">
				</button>
			</div>
		</div>

		<!-- Dialog errore per appuntamento non trovato o completato -->
		<notice-dialog :is-visible="showErrorNotice" title="Errore" :content="errorMessage"
			confirm-button-text="Torna alla Home" @confirm="redirectToHome" @close="hideErrorNotice"
			:close-on-backdrop="!isProcessing" />

		<!-- Dialog conferma eliminazione -->
		<notice-dialog :is-visible="showDeleteConfirmation" title="Conferma eliminazione"
			content="Sei sicuro di voler eliminare questo appuntamento? Questa azione non può essere annullata."
			confirm-button-text="Elimina appuntamento" cancel-button-text="Annulla" @confirm="deleteAppointment"
			@close="hideDeleteConfirmation" :show-cancel-button="true" :close-on-backdrop="!isProcessing"
			:confirm-disabled="isProcessing" />

		<!-- Dialog successo per operazioni completate -->
		<notice-dialog :is-visible="showSuccessNotice" title="Successo" :content="successMessage"
			confirm-button-text="Torna alla Home" second-button-text="Prenota un altro appuntamento"
			:show-second-button="showBookNewButton" @confirm="redirectToHome" @second-action="bookNewAppointment"
			@close="hideSuccessNotice" :close-on-backdrop="!isProcessing" />

		<!-- Overlay per bloccare l'interazione durante l'elaborazione -->
		<div class="processing-overlay" v-if="isProcessing">
			<div class="spinner"></div>
		</div>
	</div>
</template>

<script>
import AppointmentPurpose from "./AppointmentPurpose.vue";
import AppointmentNotice from "./AppointmentNotice.vue";
import StoreSelector from "./StoreSelector.vue";
import DateTimeSelector from "./DateTimeSelector.vue";
import CustomerInfoForm from "./CustomerInfoForm.vue";
import AppointmentConfirmation from "./AppointmentConfirmation.vue";
import NoticeDialog from "./common/NoticeDialog.vue";
import { mapState, mapActions } from "vuex";
import { translate } from "../utils/translation.js";
import arrowIcon from '@/assets/images/arrow-brown-icon.svg';
import arrowIconWhite from '@/assets/images/arrow-white-icon.svg';

export default {
	name: "AppointmentWizard",

	components: {
		AppointmentPurpose,
		AppointmentNotice,
		StoreSelector,
		DateTimeSelector,
		CustomerInfoForm,
		AppointmentConfirmation,
		NoticeDialog,
	},

	data () {
		return {
			currentStep: 1,
			arrowIcon,
			arrowIconWhite,
			steps: [
				{
					id: 1,
					title: "Appointment purpose",
					translationKey: "appointment.appointment_purpose",
				},
				{ id: 2, title: "Please note", translationKey: "appointment.please_note" },
				{ id: 3, title: "Please note", translationKey: "appointment.please_note" },
				{
					id: 4,
					title: "Select a point of sale",
					translationKey: "appointment.select_pos",
				},
				{
					id: 5,
					title: "Please choose a date and time",
					translationKey: "appointment.choose_date_time",
				},
				{
					id: 6,
					title: "Please enter your contact information",
					translationKey: "appointment.enter_contact_info",
				},
				{
					id: 7,
					title: "Your appointment is confirmed",
					translationKey: "appointment.appointment_confirmed",
				},
			],
			noticeAccepted: {
				availability: false,
				service: false,
			},
			selectedPurpose: null,
			selectedCountry: null,
			selectedStore: null,
			selectedDate: null,
			selectedTime: null,
			selectedTimeSlot: null,
			editMode: false,
			appointmentId: null,
			customerInfo: null,
			termsContent: "Inserisci qui i termini e condizioni...",
			privacyContent: "Inserisci qui la privacy policy...",
			customerFormValid: false,
			showErrorNotice: false,
			errorMessage: "",
			showSuccessNotice: false,
			successMessage: "",
			showDeleteConfirmation: false,
			isProcessing: false,
			isTrackingReady: false,
			showBookNewButton: false,
		};
	},

	computed: {
		...mapState({
			storeLocations: (state) => state.locations.stores,
			purposes: (state) => state.appointment.purposes,
			notices: (state) => state.appointment.notices,
			availableDates: (state) => state.appointment.availableDates,
			availableTimes: (state) => state.appointment.availableTimes,
			currentAppointment: (state) => state.appointment.currentAppointment,
			storeError: (state) => state.appointment.error,
			showErrorDialog: (state) => state.appointment.showErrorDialog,
			trackingEnabled: (state) => state.rolexTracking.trackingEnabled,
		}),

		canProceed () {
			switch (this.currentStep) {
				case 1:
					return !!this.selectedPurpose;
				case 2:
					return this.noticeAccepted.availability;
				case 3:
					return this.noticeAccepted.service;
				case 4:
					return !!this.selectedStore;
				case 5:
					return !!this.selectedDate && !!this.selectedTime;
				case 6:
					return this.customerFormValid;
				default:
					return true;
			}
		},

		appointmentDetails () {
			return {
				id: this.appointmentId,
				purpose: this.selectedPurpose,
				store: this.selectedStore,
				date: this.selectedDate,
				time: this.selectedTime,
				notifyEarlier:
					this.editMode && this.currentAppointment
						? this.currentAppointment.customer.notifyEarlier
						: false,
			};
		},
		isPurposeStep () {
			return this.currentStep === 1;
		},
		isNoticeStep () {
			return this.currentStep === 2 || this.currentStep === 3;
		},
	},

	created () {
		// Initialize data
		this.fetchStoreLocations();
		this.fetchPurposes();

		// inizializza l'ascolto dell'evento rolexTrackingReady
		window.addEventListener("rolexTrackingReady", this.handleTrackingReady);

		// Check if we're editing an existing appointment
		const urlParams = new URLSearchParams(window.location.search);
		this.appointmentId = urlParams.get("appointment_id");
		const token = urlParams.get("token");
		const action = urlParams.get("action");

		if (this.appointmentId) {
			if (action === "delete") {
				this.loadAppointment({ id: this.appointmentId, token })
					.then(() => {
						// se il caricamento è riuscito allora si mostra la dialog
						this.showDeleteConfirmation = true;
					})
					.catch((error) => {
						console.error("Error loading appointment:", error);
					});
			} else {
				this.editMode = true;
				this.loadAppointment({ id: this.appointmentId, token }).catch((error) => {
					// La gestione dell'errore avviene automaticamente tramite lo store
					// e il watcher showErrorDialog
					console.error("Error loading appointment:", error);
				});
			}
		}
	},

	watch: {
		showErrorDialog (newVal) {
			if (newVal && this.storeError) {
				this.errorMessage = this.storeError;
				this.showErrorNotice = true;

				// Se l'errore è relativo a un appuntamento completato o non trovato,
				// prepara il componente per resettare lo stato quando il dialog viene chiuso
				if (
					this.storeError.includes("già passato") ||
					this.storeError.includes("non trovato")
				) {
					// In alternativa, si potrebbe resettare direttamente qui
					// this.resetAppointmentState();
				}
			}
		},

		// Watch per la data di appuntamento selezionata
		selectedDate (newVal) {
			if (newVal) {
				// Verifica se la data selezionata è nel passato
				const today = new Date();
				today.setHours(0, 0, 0, 0);

				const selectedDate = new Date(newVal);
				selectedDate.setHours(0, 0, 0, 0);

				if (selectedDate < today) {
					this.errorMessage = "Non è possibile selezionare una data nel passato.";
					this.showErrorNotice = true;

					// Reset della data selezionata
					this.$nextTick(() => {
						this.selectedDate = null;
					});
				}
			}
		},
	},

	beforeDestroy () {
		// Rimuovi l'event listener quando il componente viene distrutto
		window.removeEventListener("rolexTrackingReady", this.handleTrackingReady);
	},

	methods: {
		...mapActions({
			fetchStoreLocations: "locations/fetchStoreLocations",
			fetchPurposes: "appointment/fetchPurposes",
			fetchAvailableDates: "appointment/fetchAvailableDates",
			fetchAvailableTimes: "appointment/fetchAvailableTimes",
			createAppointment: "appointment/createAppointment",
			updateAppointment: "appointment/updateAppointment",
			loadAppointment: "appointment/loadAppointment",
			clearError: "appointment/clearError",
			resetAppointmentData: "appointment/resetAppointmentData",
			deleteAppointmentAction: "appointment/deleteAppointment",
			// tracciamento
			initTracking: "rolexTracking/initTracking",
			trackBookAppointment: "rolexTracking/trackBookAppointment",
			trackModifyAppointment: "rolexTracking/trackModifyAppointment",
			trackNewAppointment: "rolexTracking/trackNewAppointment",
			trackCancelAppointment: "rolexTracking/trackCancelAppointment",
		}),

		handleTrackingReady (event) {
			console.log("rolexTrackingReady event received", event.detail);
			this.isTrackingReady = true;
		},

		selectPurpose (purpose) {
			this.selectedPurpose = purpose;
		},

		acceptAvailabilityNotice () {
			this.noticeAccepted.availability = true;
		},

		acceptServiceNotice () {
			this.noticeAccepted.service = true;
		},

		selectCountry (country) {
			this.selectedCountry = country;
		},

		selectStore (store) {
			this.selectedStore = store;
			// Fetch available dates for this store
			this.fetchAvailableDates({
				storeId: store.id,
				purposeId: this.selectedPurpose.id,
				appointmentId: this.editMode ? this.appointmentId : null,
			});
		},

		selectDate (date) {
			this.selectedDate = date;
			// Fetch available times for this date and store
			this.fetchAvailableTimes({
				storeId: this.selectedStore.id,
				purposeId: this.selectedPurpose.id,
				date: date,
				appointmentId: this.editMode ? this.appointmentId : null,
			});
		},

		selectTime (timeObj) {
			// Assegna il valore correttamente
			this.selectedTime = timeObj.value;

			// Salva l'ID dello slot per il salvataggio
			this.selectedTimeSlot = timeObj.id;
		},

		updateFormValidity (isValid) {
			this.customerFormValid = isValid;
		},

		prevStep () {
			if (this.currentStep > 1) {
				this.currentStep -= 1;
			}
		},

		nextStep () {
			if (this.canProceed) {
				if (this.currentStep < 7) {
					this.currentStep += 1;
				}

				// Aspetta che il DOM venga aggiornato e poi riporta l'inizio del wizard in vista
				if (window.location.pathname.includes('/calendario')) {
					this.$nextTick(this.scrollToWizardTop);
				}

				// If moving to confirmation step, save the appointment
				if (this.currentStep === 7) {
					this.saveAppointment();
				}
			}
		},

		scrollToWizardTop () {
			const wizard = this.$el;
			if (!wizard) return;

			// Altezza di un eventuale header fisso/sticky del tema da non coprire
			let offset = 0;
			const header = document.querySelector("header");
			if (header) {
				const position = window.getComputedStyle(header).position;
				if (position === "fixed" || position === "sticky") {
					offset = header.offsetHeight;
				}
			}

			const top = wizard.getBoundingClientRect().top + window.pageYOffset - offset - 20;
			window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
		},

		saveAppointment () {
			// Ottieni i dati del cliente dal form
			const customerInfo = this.$refs.customerInfoForm.getCustomerInfo();

			const appointmentData = {
				purpose_id: this.selectedPurpose.id,
				store_id: this.selectedStore.id,
				date: this.selectedDate,
				time_slot: this.selectedTimeSlot,
				customer_title: customerInfo.title,
				customer_first_name: customerInfo.firstName,
				customer_last_name: customerInfo.lastName,
				customer_email: customerInfo.email,
				customer_phone_code: customerInfo.phoneCode,
				customer_phone: customerInfo.phone,
				customer_country: customerInfo.country,
				customer_city: customerInfo.city,
				customer_notes: customerInfo.notes,
				customer_terms_accepted: customerInfo.termsAccepted,
			};

			if (this.editMode && this.appointmentId) {
				const urlParams = new URLSearchParams(window.location.search);
				const token = urlParams.get("token");

				this.updateAppointment({
					id: this.appointmentId,
					data: appointmentData,
					token: token
				})
					.then(() => {
						this.$rolexTracking.trackModifyAppointment({
							purpose: this.selectedPurpose,
							date: this.selectedDate,
							time: this.selectedTime,
						});
					})
					.catch((error) => {
						// Gestire qui eventuali errori nel salvataggio
						this.errorMessage =
							"Si è verificato un errore durante l'aggiornamento dell'appuntamento. Riprova più tardi.";
						this.showErrorNotice = true;
					});
			} else {
				this.createAppointment(appointmentData)
					.then((response) => {
						this.appointmentId = response.id;
						// Once we create the appointment, set edit mode to true
						// so subsequent modifications will update, not create
						this.editMode = true;

						// Traccia la creazione dell'appuntamento
						this.$rolexTracking.trackBookAppointment({
							purpose: this.selectedPurpose,
							date: this.selectedDate,
							time: this.selectedTime,
						});
					})
					.catch((error) => {
						console.error("Error creating appointment:", error);

						// Gestione di vari tipi di errore
						if (error.isApiError && error.type === "date_in_past") {
							this.errorMessage =
								"Non è possibile creare un appuntamento per una data già passata.";
						} else {
							this.errorMessage =
								"Si è verificato un errore durante la creazione dell'appuntamento. Riprova più tardi.";
						}

						this.showErrorNotice = true;
					});
			}
		},

		addToCalendar (type) {
			// Handle calendar integration (Google, Outlook, etc.)
			const calendarData = {
				title: `Rolex Appointment - ${this.selectedPurpose.name}`,
				description: `Your appointment at ${this.selectedStore.name}`,
				location: this.selectedStore.address,
				startDate: new Date(`${this.selectedDate}T${this.selectedTime}`),
				endDate: new Date(`${this.selectedDate}T${this.selectedTime}`),
				type: type, // 'google', 'outlook', etc.
			};

			// Import calendar service
			const calendarService = require("../services/calendar.js");
			calendarService.generateCalendarLink(calendarData);
		},

		modifyAppointment () {
			// Reset to step 1 to allow modification
			this.currentStep = 1;

			// Ensure we're in edit mode and appointment ID is set
			// This is crucial - we need to make sure we're updating, not creating
			this.editMode = true;
		},

		hideErrorNotice () {
			this.showErrorNotice = false;
			this.clearError();

			// Se l'errore era relativo a un appuntamento completato, resettiamo lo stato
			if (
				this.errorMessage.includes("già passato") ||
				this.errorMessage.includes("non trovato")
			) {
				this.resetAppointmentState();
			}
		},

		resetAppointmentState () {
			// Reset completo dello stato del wizard
			this.editMode = false;
			this.appointmentId = null;
			this.currentStep = 1;
			this.selectedPurpose = null;
			this.selectedCountry = null;
			this.selectedStore = null;
			this.selectedDate = null;
			this.selectedTime = null;
			this.selectedTimeSlot = null;
			this.noticeAccepted = {
				availability: false,
				service: false,
			};

			// Reset dello stato nello store
			this.resetAppointmentData();
		},

		redirectToHome () {
			// Prima di reindirizzare, resettiamo lo stato
			this.resetAppointmentState();

			// Reindirizza alla homepage o alla pagina principale dell'applicazione
			window.location.href = "/";
			// In alternativa, se utilizzi Vue Router:
			// this.$router.push('/');
		},

		// Verifica se la data selezionata è valida (non nel passato)
		isValidDate (dateString) {
			if (!dateString) return false;

			const today = new Date();
			today.setHours(0, 0, 0, 0);

			const date = new Date(dateString);
			date.setHours(0, 0, 0, 0);

			return date >= today;
		},

		hideDeleteConfirmation () {
			if (this.isProcessing) return; // Non chiudere se è in corso l'elaborazione
			this.showDeleteConfirmation = false;
		},

		hideSuccessNotice () {
			this.showSuccessNotice = false;
		},

		deleteAppointment () {
			const urlParams = new URLSearchParams(window.location.search);
			const token = urlParams.get("token");

			// attiva lo stato di elaborazione
			this.isProcessing = true;

			this.deleteAppointmentAction({
				id: this.appointmentId,
				token: token,
			})
				.then(() => {
					// disattiva lo stato di elaborazione
					this.isProcessing = false;

					// nasconde il dialog di conferma
					this.showDeleteConfirmation = false;
					this.successMessage = "L'appuntamento è stato eliminato con successo.";

					// Attiva il pulsante per prenotare un nuovo appuntamento
					this.showBookNewButton = true;

					this.showSuccessNotice = true;
					this.resetAppointmentState();
					this.currentStep = 1;

					this.$rolexTracking.trackCancelAppointment();
				})
				.catch((error) => {
					console.log(error);
					this.isProcessing = false;
					this.errorMessage =
						"Si è verificato un errore durante l'eliminazione dell'appuntamento.";
					this.showErrorNotice = true;
					this.showDeleteConfirmation = false;
				});
		},

		bookNewAppointment () {
			this.$rolexTracking.trackNewAppointment();
			this.resetAppointmentState();
			this.currentStep = 1;
			this.showBookNewButton = false;
		},
	},
};
</script>

<style>
/* Stili per l'overlay di elaborazione */
.processing-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(255, 255, 255, 0.7);
	z-index: 9999;
	display: flex;
	justify-content: center;
	align-items: center;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 4px solid rgba(10, 87, 65, 0.3);
	border-radius: 50%;
	border-top: 4px solid #0a5741;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(360deg);
	}
}
</style>
